import config from "config.json";
import { getCookie } from "utils/cookie";
import {
	getDefaultLocale,
	localeRegExp,
	getDefaultLocaleCookie,
	getDefaultLocaleCookieProperty,
	getGDPRLocale,
} from "utils/language";

const normalizeLocale = (l) => l.toLowerCase().replace("_", "-");

let currentScript, urlParams, url;
const brands = {
	disneyland: "dlr",
	disneyworld: "wdw",
	disneycruise: "dcl",
	beachresorts: "beachresorts",
	disneyaulani: "aulani",
	hiltonhead: "hh",
	hongkongdisneyland: "hkdl",
	verobeach: "vb",
	disneyvacationclub: "dvc",
	dvcmember: "dvc",
	disneyparks: "dp",
};
const storeParam = "store";
const storesStartPath = "reservations";
const routesWithStoresConfiguration = [
	"/header",
	"/footer",
	"/legal",
	"/message-center",
];
const messageCenterId = "message-center";
const messageCenterParam = "messageCenter";
const travelAgentHeaderId = "travel-agent-header";
const travelAgentHeaderParam = "travelAgentHeader";

if (process.env.NODE_ENV === "production") {
	currentScript = document?.currentScript;
}

const loadAppConfiguration = () => {
	url = new URL(currentScript ? currentScript.src : window?.location.href);
	const isMessageCenter = document?.getElementById(messageCenterParam);
	if (isMessageCenter) {
		url.searchParams.append(messageCenterId, true);
		url.searchParams.append(messageCenterParam, false);
		url.searchParams.append(`${messageCenterId}Container`, messageCenterParam);
	}
	const isTravelAgentHeader = document?.getElementById(travelAgentHeaderParam);
	if (isTravelAgentHeader) {
		url.searchParams.append(travelAgentHeaderId, true);
		url.searchParams.append(travelAgentHeaderParam, false);
		url.searchParams.append(
			`${travelAgentHeaderId}Container`,
			travelAgentHeaderParam
		);
	}
	urlParams = url.searchParams;
};

const getParam = (paramName) => {
	return urlParams.get(paramName)?.toLowerCase() || "";
};

const getStore = () => {
	let store = getParam("store");
	const pathname = window?.location.pathname;
	const brand = loadBrandParam();
	const brandConfig = config.brands?.[brand] || {};
	if (!store && pathname.includes(`/${storesStartPath}/`)) {
		const pathnameParts = pathname.split("/");
		const storesStartIndex = pathnameParts.findIndex(
			(subpath) => subpath === storesStartPath
		);
		if (storesStartIndex !== -1 && pathnameParts[storesStartIndex + 1]) {
			store = pathnameParts[storesStartIndex + 1];
		}
	}
	if (brandConfig.storeScope?.includes(store)) {
		return "";
	}
	return store;
};

const addParam = (name, value) => {
	urlParams.append(name, value);
};

const validateMessageCenterParam = (
	paramName,
	brand = "dlr",
	locale = "en-us"
) => {
	let loadMessageCenter = validateParam(paramName, false);
	if (!loadMessageCenter) {
		loadMessageCenter = true;
		const brandConfig = config.brands?.[brand] || {};
		if (
			brandConfig.preventLoadingMessageCenter ||
			brandConfig.configurationsByLocale?.[locale]?.preventLoadingMessageCenter
		) {
			loadMessageCenter = false;
		}
	}
	return loadMessageCenter;
};

const validateParam = (paramName, defaultValue = false) => {
	const param = getParam(paramName);
	return param ? param === "true" || param === "1" : defaultValue;
};

const validateParamValue = (paramName, valuesArray, defaultIndex = 0) => {
	const param = getParam(paramName);
	return param && valuesArray.includes(param)
		? param
		: valuesArray[defaultIndex];
};

const validateSubBrands = (brand, pathname) => {
	const subBrandsPaths = config.brands[brand]?.subBrandsPaths;
	if (subBrandsPaths) {
		for (const subBrandKey in subBrandsPaths) {
			if (pathname.includes(`/${subBrandKey}/`)) {
				return subBrandsPaths[subBrandKey];
			}
		}
	}
	return brand;
};

const loadBrandParam = () => {
	const { hostname, pathname } = window?.location;
	const splittedHostname = hostname.split(".");
	for (const brandKey in brands) {
		if (splittedHostname.includes(brandKey)) {
			return validateSubBrands(brands[brandKey], pathname);
		}
	}
	return validateSubBrands(
		validateParamValue("brand", Object.keys(config.brands)),
		pathname
	);
};

const getLocalParamFromPath = () => {
	return window?.location.pathname.match(localeRegExp)?.[0].replace(/\//g, "");
};

const loadLocaleParam = () => {
	const brand = loadBrandParam();
	const customLocale = getParam("customLocale");
	let locale = getParam("locale") || getDefaultLocale(brand);

	if (customLocale) {
		locale = customLocale;
	} else {
		const rawPathLocale = getLocalParamFromPath();
		let parsedLocale = rawPathLocale || null;
		let cookie = null;
		let localeCookieName;
		const store = getStore();

		const preventReadLocalCookie =
			store ||
			config.brands[brand].preventReadingLocaleFromCookie ||
			config.brands[brand]?.preventReadingLocaleFromCookieIfLocaleIs?.includes(
				parsedLocale
			);

		if (!preventReadLocalCookie) {
			localeCookieName = getDefaultLocaleCookie(brand);
			cookie =
				getCookie(`${localeCookieName}_aka`) || getCookie(localeCookieName);
		}

		if (!cookie) {
			if (parsedLocale) {
				locale = parsedLocale;
			} else {
				parsedLocale = getGDPRLocale(brand);
				if (parsedLocale) {
					locale = parsedLocale;
				}
			}
		} else if (cookie) {
			try {
				const cookieJarObj = JSON.parse(decodeURIComponent(cookie));
				const cookieProperty = getDefaultLocaleCookieProperty(brand);
				const cookieLocale = cookieJarObj[cookieProperty];

				if (parsedLocale && normalizeLocale(parsedLocale) !== (cookieLocale ? normalizeLocale(cookieLocale) : undefined)) {
					locale = parsedLocale;
				} else if (cookieLocale) {
					locale = cookieLocale;
				}
			} catch (error) {
				console.info(`unable to parse the ${localeCookieName}`);
			}
		}
	}

	const [language, region] = normalizeLocale(locale).split("-");
	const currentLocale = `${language}${region ? "-" + region : ""}`;
	return (
		config?.brands?.[brand]?.allowedLocales?.[currentLocale] || currentLocale
	);
};

const validateParams = (params = [], setIsHeaderLoading) => {
	let valid = false;
	for (let index = 0; index < params.length && !valid; index++) {
		valid = validateParam(params[index]);
	}
	if (!valid && setIsHeaderLoading) setIsHeaderLoading(false);

	return valid;
};

const validateAndAddStoreParam = (url) => {
	if (routesWithStoresConfiguration.some((key) => url.href.includes(key))) {
		const store = getStore();
		if (store) {
			url.searchParams.append(storeParam, store);
		}
	}
};

const loadAffiliations = () => {
	const affiliationParam = getParam("affiliation").toUpperCase();
	return affiliationParam ? affiliationParam.split("-") : [];
};

const validateBodyClasses = (classesList) => {
	if (!classesList) return false;

	const bodyClassList = document?.body?.classList;
	if (!bodyClassList) return false;

	return classesList
		.split(".")
		.every((cls) => cls && bodyClassList.contains(cls));
};

const getFhhPosition = () => {
	const customChatPosition = getParam("fhhPosition");
	const validPositions = ["top", "middle", "bottom"];
	if (validPositions.includes(customChatPosition)) {
		return customChatPosition;
	}
	return "middle";
}

loadAppConfiguration();

export {
	addParam,
	currentScript,
	getParam,
	getStore,
	loadBrandParam,
	loadLocaleParam,
	loadAffiliations,
	urlParams,
	validateParam,
	validateParams,
	validateParamValue,
	validateAndAddStoreParam,
	loadAppConfiguration,
	validateMessageCenterParam,
	validateBodyClasses,
	getFhhPosition,
};
